interface AccessServiceOptions {
    ignoreAdmin?: boolean
    onlyAdmin?: boolean
    payload?: any
}

interface AccessInfo {
    _id: string,
    role: string,
}

const accessInfo = {
    _id: 'xxx',
    role: 'guest'
}

export const setAccessInfo = (newAccessInfo: { _id?: string, role?: string }) => {
    if (newAccessInfo._id) {
        accessInfo._id = newAccessInfo._id
    }
    if (newAccessInfo.role) {
        accessInfo.role = newAccessInfo.role
    }
}

export const resetAccessInfo = () => {
    accessInfo._id = 'xxx'
    accessInfo.role = 'guest'
}

export default function canAccess(resource: string = 'xxx', action: string = 'xxx', options: AccessServiceOptions = {}) {
    const role = accessInfo.role
    if (role === 'admin' && !options.ignoreAdmin) {
        return true
    }

    if (options.onlyAdmin) {
        return false
    }

    let accessible: boolean = false

    if (ROLE_VALUES[role] && ROLE_VALUES[role][resource] && ROLE_VALUES[role][resource][action]) {
        accessible = ROLE_VALUES[role][resource][action]
    }

    if (accessible && options.payload) {
        const resourceRules = RESOURCE_PAYLOAD_RULES[resource]
        if (resourceRules) {
            const actionRule = resourceRules[action]
            if (actionRule) {
                const ruleFunction = actionRule[accessInfo.role]
                if (ruleFunction) {
                    accessible = ruleFunction(options.payload, accessInfo)
                }
            }
        }
    }

    return accessible
}

export function getRoleValues(role: string) {
    return ROLE_VALUES[role] || {}
}

export function getRole() {
    return accessInfo.role
}



// VARIABLES

interface ResourcePayloadRules {
    [resource: string]: {
        [action: string]: {
            [role: string]: (payload: any, accessInfo: AccessInfo) => boolean
        }
    }
}

const myBooking = (booking: any, accessInfo: AccessInfo) => {
    return booking && booking.createdBy === accessInfo._id
}

const RESOURCE_PAYLOAD_RULES: ResourcePayloadRules = {
    'routes': {
        'edit-booking': {
            'owner': myBooking,
        }
    },
    'booking': {
        'bookingGuestLink': {
            'owner': myBooking,
        },
        'email-actions': {
            'owner': myBooking,
        }
    }
}

const STANDARD_HOME_ACTIONS = {
    'link.cleaning': true,
    'link.tasks': true,
}

const STANDARD_ROUTES = {
    'account': true,
    'login': true,
    'register': true,
    'forgot-password': true,

    'cleaning': true,
    'property-task-list': true,
    'inventory-check': true,
    'confirm-booking-guests': true,
    'my-notifications': true,
    'notification-entry': true,
}

const ROLE_VALUES: any = {
    accounting: {
        'routes': {
            ...STANDARD_ROUTES,
            'property': true,
            'property-bookings': true,
            'my-properties': true,
            "financials-property": true,

            "property-task-reporter": true,

            "owner-turnovers": true,

            "handle-property-prices": true,

            'bookings': true,
            'booking': true,
            'add-booking': true,
            'edit-booking': true,
            'add-booking-quick': true,

            "unconfirmed-bookings": true,
            "upcoming-bookings": true,

            "adr-occupancy-reporter": true,

            'booking-guest': true,
            'booking-guests': true,

            "purchases": true,
            "purchase": true,

            'ical-data': true,

            "bukins": true,
            "bukin": true,

            "bukibles": true,
            "bukible": true,

            "additional-services": true,
            "additional-service": true,
            "add-additional-service": true,
            "edit-additional-service": true,
        },
        'menu': {
            'bookings': true,
            'date-blocks': true,
            'booking-guests': true,
            'properties': true,
            "bukibles": true,
            "bukins": true,
            "property-tasks": true,
            'users': true,
            "additional-services": true,
        },
        'home': {
            ...STANDARD_HOME_ACTIONS,
            'mainLinks': true,
            'link.guests': true,
            'link.bukibles': true,
            'propertyList': true,
            'link.reports': true,
        },
        "booking": {
            'edit-booking-time': true,
            'airbnbReservationDetails': true,
            'vrboReservationDetails': true,
        },
        'bookingGuest': {
            'mode.guest': true,
            'mode.booking': true,

            'confirmed': true,
            'needsInfo': true,
            'pendingSend': true,
            'pendingConfirm': true,
        },
        "user": {}
    },
    cleaning: {
        'routes': {
            ...STANDARD_ROUTES,
        },
        'home': {
            ...STANDARD_HOME_ACTIONS,
            'link.guests': true,
        }
    },
    casual: {
        'routes': {
            ...STANDARD_ROUTES,
        },
        'home': {
            ...STANDARD_HOME_ACTIONS,
            'link.guests': true,
        },
        "bookingGuest": {
            'mode.booking': true,
            'mode.guest': true,

            'all': true,
            'needsInfo': true,
            'pendingSend': true,
            'pendingConfirm': true,
            'confirmed': true,

            'markConfirm': true,
            'markPending': true,
            'markDenied': true,
            'sendConfirm': true,
        }
    },
    'front-desk': {
        'routes': {
            ...STANDARD_ROUTES,
            'property': true,
            "edit-property": true,
            'property-bookings': true,
            'my-properties': true,
            "financials-property": true,


            "handle-property-prices": true,

            'bookings': true,
            'booking': true,
            "add-booking": true,
            "add-booking-quick": true,
            'edit-booking': true,

            "booking-messages": true,
            "view-booking-messages": true,

            "unconfirmed-bookings": true,
            "upcoming-bookings": true,

            "adr-occupancy-reporter": true,

            "fontaine-tools": true,

            'date-blocks': true,
            'date-block': true,
            'add-date-block': true,
            'edit-date-block': true,

            'booking-guest': true,
            'booking-guests': true,
            'edit-booking-guest': true,
            'delete-booking-guest': true,

            'ical-data': true,

            "bukins": true,
            "bukin": true,
            "add-bukin": true,
            "edit-bukin": true,

            "bukibles": true,
            "bukible": true,

            "purchases": true,
            "purchase": true,

            "additional-services": true,
            "additional-service": true,
            "add-additional-service": true,
            "edit-additional-service": true,
        },
        'menu': {
            'bookings': true,
            'date-blocks': true,
            'booking-guests': true,
            'properties': true,
            "bukibles": true,
            "bukins": true,
            'tools': true,
            'purchases': true,
            'additional-services': true,
        },
        'home': {
            ...STANDARD_HOME_ACTIONS,
            'mainLinks': true,
            'link.guests': true,
            'link.bukibles': true,
            'propertyList': true,
            'link.reports': true,
            'link.bookingMessages': true,
        },
        "booking": {
            'form.requiresConfirmation': true,
            'airbnbReservationDetails': true,
            'vrboReservationDetails': true,
            'edit-booking-time': true,
            'email-actions': true,
        },
        'bookingGuest': {
            'mode.guest': true,
            'mode.booking': true,

            'data.financial': true,

            'all': true,
            'confirmed': true,
            'needsInfo': true,
            'pendingSend': true,
            'pendingConfirm': true,

            'markConfirm': true,
            'markPending': true,
            'markDenied': true,
            'sendConfirm': true,
        },
        "tasks": {
            "autoApprove": true,
            "completeTask": true,
        },
        "property": {
            "direct-offer": true,
            "edit-airbnb": true,
            "door-controls": true,
        },
        "user": {}
    },
    maintenance: {
        'routes': {
            ...STANDARD_ROUTES,
        },
        'home': {
            ...STANDARD_HOME_ACTIONS,
        }
    },
    'maintenance-lead': {
        'routes': {
            ...STANDARD_ROUTES,
        },
        'home': {
            ...STANDARD_HOME_ACTIONS,
        },
        "tasks": {
            "autoApprove": true,
        },
    },
    owner: {
        'routes': {

            // user stuff
            'account': true,
            'login': true,
            'register': true,
            'forgot-password': true,

            'property': true,
            'properties': true,
            'property-bookings': true,

            "adr-occupancy-reporter": true,
            "financials-property": true,

            "add-booking": true,
            "edit-booking": true,
            'bookings': true,
            'booking': true,
            "upcoming-bookings": true,
        },
        'menu': {
            'bookings': true,
        },
        'home': {
            'link.bookings': true,
            'propertyList': true,
            'link.reports': true,
            'link.availableUpcoming': true,
        },
        'booking': {
            'bookingGuestLink': true,
        }
    },
    viewer: {
        'routes': {
            ...STANDARD_ROUTES,
            'property': true,
            'property-bookings': true,
            'my-properties': true,

            "property-task-reporter": true,

            'bookings': true,
            'booking': true,

            "upcoming-bookings": true,
        },
        'menu': {
            'bookings': true,
            'properties': true,
            "property-tasks": true,
        },
        'home': {
            ...STANDARD_HOME_ACTIONS,
            'mainLinks': true,
            'propertyList': true,
        },
        "booking": {
        },
        'bookingGuest': {},
        "user": {}
    },
}

